import React, { useState, useEffect } from "react"
import { Table, Space, Tag, Typography, message, Row, Col } from "antd"
import { collection, onSnapshot, query, orderBy } from "firebase/firestore"
import { DownloadOutlined, FileDoneOutlined, FileTextFilled } from "@ant-design/icons"
import { db } from "../../firebase/config"
import PageHeader from "../../components/Elements/PageHeader"

const { Text } = Typography

export default function AllPackingLists() {
  const [packingLists, setPackingLists] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Create query to get packing lists ordered by creation date
    const packingListsRef = collection(db, "packingLists")
    const q = query(packingListsRef, orderBy("created", "desc"))

    // Subscribe to query
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const lists = []
        snapshot.forEach((doc) => {
          lists.push({ ...doc.data(), id: doc.id })
        })
        setPackingLists(lists)
        setLoading(false)
      },
      (error) => {
        console.error("Error fetching packing lists:", error)
        message.error("Failed to load packing lists")
        setLoading(false)
      }
    )

    // Cleanup subscription
    return () => unsubscribe()
  }, [])

  const columns = [
    {
      title: "Packing List #",
      dataIndex: "packingListNumber",
      key: "packingListNumber",
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: "Created",
      dataIndex: "created",
      key: "created",
      render: (timestamp) => {
        if (!timestamp) return "-"
        const date = timestamp.toDate()
        return date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      },
    },
    {
      title: "Sender",
      dataIndex: "sender",
      key: "sender",
      render: (sender) => sender?.name || "-",
    },
    {
      title: "Receiver",
      dataIndex: "receiver",
      key: "receiver",
      render: (receiver) => receiver?.name || "-",
    },
    {
      title: "Products",
      dataIndex: "products",
      key: "products",
      render: (products) => <span>{products?.length || 0} items</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        let color = "default"
        switch (status) {
          case "created":
            color = "blue"
            break
          case "shipped":
            color = "green"
            break
          case "cancelled":
            color = "red"
            break
          default:
            color = "default"
        }
        return <Tag color={color}>{status || "unknown"}</Tag>
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space size="middle">
          <a
            href={record.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              e.preventDefault()
              window.open(record.url, "_blank")
            }}>
            <DownloadOutlined /> PDF
          </a>
          {record.invoiceUrl && (
            <a
              href={record.invoiceUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                e.preventDefault()
                window.open(record.invoiceUrl, "_blank")
              }}>
              <FileDoneOutlined /> Invoice
            </a>
          )}
        </Space>
      ),
    },
  ]

  return (
    <div style={{ padding: 15 }}>
      <Row gutter={[15, 15]}>
        <Col span={24}>
          <PageHeader text="Packing Lists" icon={<FileTextFilled />} />
        </Col>
        <Col span={24}>
          <Table
            columns={columns}
            dataSource={packingLists}
            rowKey="id"
            loading={loading}
            pagination={{
              defaultPageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} packing lists`,
            }}
          />
        </Col>
      </Row>
    </div>
  )
}
